import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CustomInputComponent } from '../custom-input/custom-input.component';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { UnknownVoidFunction } from '../../types/unknown-void-function.type';
import { isValidDate } from '../../utils/is-valid-date';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import moment, { Moment } from 'moment';
import { preventFutureDateValidator } from 'src/app/form-configs/validators/prevent-future-date-validator';
import { registerV2Enum } from 'src/app/appsettings';

@Component({
  selector: 'app-custom-date-input',
  standalone: true,
  imports: [
    CustomInputComponent,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
  ],
  templateUrl: './custom-date-input.component.html',
  styleUrl: './custom-date-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDateInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomDateInputComponent),
      multi: true,
    },
  ],
})
export class CustomDateInputComponent
  implements OnInit, OnDestroy, Validator, ControlValueAccessor
{
  @ViewChild('dateInput')
  public dateInput: ElementRef<HTMLInputElement>;

  @Input()
  public label: string;

  @Input()
  public required: boolean;

  @Input()
  public placeholder: string;

  @Input()
  public disabled: boolean;

  @Output()
  public disabledChange = new EventEmitter<boolean>();

  @Input()
  public isValidDate: boolean;

  @Output()
  public isValidDateChange = new EventEmitter<boolean>();

  @Input()
  public disableFutureDates = true;

  public control = new FormControl();

  public pickerControl = new FormControl();

  public inputPlaceholder: string;

  private readonly onDestroy$ = new Subject<void>();

  private readonly filterFormat = 'DD.MM.YYYY';

  public readonly dateToday = new Date();

  public hasError = false;

  ngOnInit(): void {
    this.inputPlaceholder = this.placeholder;
    this.trackValueChanges();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  private trackValueChanges() {
    this.control.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(value => {
      const inputValue = (value || '').replace(/\D/g, '');
      setTimeout(() => {
        const slice1 = inputValue.slice(0, 2);
        const slice2 = inputValue.slice(2, 4);
        const slice3 = inputValue.slice(4, 8);
        let formattedValue = '';
        if (slice1) {
          formattedValue = slice1;
        }
        if (slice2) {
          formattedValue += `.${slice2}`;
        }
        if (slice3) {
          formattedValue += `.${slice3}`;
        }

        const isInvalid = !isValidDate(formattedValue);
        if (isInvalid) {
          this.pickerControl.setValue('');
          this.trackChanges('');
          return;
        }

        this.pickerControl.setValue(moment(formattedValue, 'DD.MM.YYYY'));
        this.control.setValue(formattedValue, { emitEvent: false });

        if (this.disableFutureDates) {
          this.hasError = !!preventFutureDateValidator()(this.control);
        }

        this.trackChanges(formattedValue);
      });
    });
  }

  // eslint-disable-next-line class-methods-use-this
  public onChange: UnknownVoidFunction = () => {};

  // eslint-disable-next-line class-methods-use-this
  public onTouched: UnknownVoidFunction = () => {};

  writeValue(value: string): void {
    this.control.setValue(value);
    if (value && isValidDate(value)) {
      this.pickerControl.setValue(moment(value, registerV2Enum.DATE_OF_BIRTH_FORMAT_NEW), { emitEvent: false });
    } else {
      this.pickerControl.setValue(null, { emitEvent: false });
    }
  }

  registerOnChange(fn: UnknownVoidFunction): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: UnknownVoidFunction): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.disabledChange.next(this.disabled);
  }

  validate(): ValidationErrors | null {
    return this.control.errors;
  }

  public inputFocus() {
    this.inputPlaceholder = this.filterFormat;
    this.onTouched();
  }

  public inputBlur() {
    this.inputPlaceholder = this.placeholder;
    this.onTouched();
  }

  // eslint-disable-next-line class-methods-use-this
  public onDateChange(event: MatDatepickerInputEvent<Moment>) {
    let date: Date;
    if (event.value instanceof Date) {
      date = event.value;
    } else {
      date = event.value?.toDate();
    }

    if (date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}.${month}.${year}`;
      this.control.setValue(formattedDate);
      this.trackChanges(this.control.value);
    }
  }

  public inputDateChange(event: Event) {
    const inputElement = event.target as HTMLInputElement;
    const dateValue = new Date(inputElement.value);
    const date = dateValue.getDate().toString().padStart(2, '0');
    const month = (dateValue.getMonth() + 1).toString().padStart(2, '0');
    const year = dateValue.getFullYear();
    this.control.setValue(`${date}.${month}.${year}`);
    this.trackChanges(this.control.value);
  }

  private isValidDateCheck() {
    this.isValidDate = isValidDate(this.control.value);
    this.isValidDateChange.next(this.isValidDate);

    if (this.isValidDate) {
      this.control.setErrors(null);
    } else {
      this.control.setErrors({ invalidDate: true });
    }
  }

  private trackChanges(value: string) {
    this.onChange(value);
    this.isValidDateCheck();
  }
}
